// src/components/Login.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import memberstackDOM from "@memberstack/dom";
import { getMemberstackKey } from "../utils";
import HelpFeedback from "./HelpFeedback"; // Import the HelpFeedback component
import { trackPageView, trackEvent } from "../utils/tracking";

const currentKey = getMemberstackKey();
const memberstack = memberstackDOM.init({
  publicKey: currentKey,
});

const Login = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Track page view on mount
  useEffect(() => {
    trackPageView('Login Page');
  }, []);

  // --------------- Login form states ---------------
  const [showForgotForm, setShowForgotForm] = useState(false);

  // --------------- Forgot password states ---------------
  const [forgotEmail, setForgotEmail] = useState("");
  const [resetMessage, setResetMessage] = useState("");

  // --------------- Reset code states ---------------
  const [showResetCodeForm, setShowResetCodeForm] = useState(false);
  const [resetCode, setResetCode] = useState("");

  // --------------- New password states ---------------
  const [showNewPasswordForm, setShowNewPasswordForm] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");

  // ----------------------------------------------------------------
  // 1) Normal login flow
  // ----------------------------------------------------------------
  const handleLogin = async (event) => {
    event.preventDefault();
    setLoading(true);
    setErrorMessage("");

    const email = document.getElementById("email").value;
    const password = document.getElementById("password").value;

    trackEvent('Login: Attempted', { email_domain: email.split('@')[1] });

    try {
      // 1. Log in with Memberstack
      const response = await memberstack.loginMemberEmailPassword({ email, password });

      // 2. Extract token & store
      const token = response?.data?.tokens?.accessToken;
      if (!token) {
        setErrorMessage("Login failed. Please check your credentials and try again.");
        trackEvent('Login: Failed', { reason: 'no_token' });
        return;
      }
      localStorage.setItem("memberstackToken", token);

      // 3. Check subscription status
      const currentMember = await memberstack.getCurrentMember();
      let hasActivePaidSubscription = false;

      for (const plan of currentMember.data.planConnections) {
        if (plan.status === "ACTIVE" && plan.type === "SUBSCRIPTION") {
          hasActivePaidSubscription = true;
          break;
        }
      }

      // Track successful login
      trackEvent('Login: Successful', {
        has_active_subscription: hasActivePaidSubscription,
        redirect_to: hasActivePaidSubscription ? 'my-events' : 'events'
      });

      // 4. Navigate accordingly
      if (hasActivePaidSubscription) {
        navigate("/my-events");
      } else {
        navigate("/events");
      }
    } catch (error) {
      setErrorMessage("Unable to login. Please check your credentials and network connection.");
      trackEvent('Login: Failed', {
        reason: 'exception',
        error_message: error.message
      });
    } finally {
      setLoading(false);
    }
  };

  // ----------------------------------------------------------------
  // 2) Show/hide the Forgot Password flow
  // ----------------------------------------------------------------
  const handleShowForgot = () => {
    const willShow = !showForgotForm;
    setShowForgotForm(willShow);
    setErrorMessage("");
    setResetMessage("");
    setForgotEmail("");
    setShowResetCodeForm(false);
    setResetCode("");
    setShowNewPasswordForm(false);
    setNewPassword("");
    setConfirmNewPassword("");

    if (willShow) {
      trackEvent('Login: Forgot Password Clicked');
    }
  };

  // ----------------------------------------------------------------
  // 3) Step 1 of Reset: Send reset email
  // ----------------------------------------------------------------
  const handleForgotPassword = async (event) => {
    event.preventDefault();
    if (!forgotEmail) {
      setErrorMessage("Please enter your email.");
      return;
    }

    setLoading(true);
    setErrorMessage("");
    setResetMessage("");

    try {
      await memberstack.sendMemberResetPasswordEmail({ email: forgotEmail });
      setResetMessage("A password reset code has been sent to your email.");
      setShowResetCodeForm(true);
    } catch (error) {
      setErrorMessage("Failed to send reset email. Please check your email and try again.");
    } finally {
      setLoading(false);
    }
  };

  // ----------------------------------------------------------------
  // 4) Step 2 of Reset: Confirm the code from email
  // ----------------------------------------------------------------
  const handleResetCodeSubmit = (event) => {
    event.preventDefault();
    if (!resetCode) {
      setErrorMessage("Please enter the code from your email.");
      return;
    }
    setErrorMessage("");
    setResetMessage("");
    setShowNewPasswordForm(true);
  };

  // ----------------------------------------------------------------
  // 5) Step 3 of Reset: New password -> reset
  // ----------------------------------------------------------------
  const handleNewPasswordSubmit = async (event) => {
    event.preventDefault();
    if (!newPassword || !confirmNewPassword) {
      setErrorMessage("Please fill in both password fields.");
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setErrorMessage("Passwords do not match. Please try again.");
      return;
    }

    setLoading(true);
    setErrorMessage("");

    try {
      await memberstack.resetMemberPassword({
        token: resetCode,
        newPassword: newPassword,
      });
      // After success, attempt to log them in automatically
      const response = await memberstack.loginMemberEmailPassword({
        email: forgotEmail,
        password: newPassword,
      });
      const token = response?.data?.tokens?.accessToken;
      if (!token) {
        setErrorMessage("Reset succeeded, but login failed. Please try logging in manually.");
        return;
      }

      localStorage.setItem("memberstackToken", token);

      // Check subscription
      const currentMember = await memberstack.getCurrentMember();
      let hasActivePaidSubscription = false;
      for (const plan of currentMember.data.planConnections) {
        if (plan.status === "ACTIVE" && plan.type === "PAID") {
          hasActivePaidSubscription = true;
          break;
        }
      }

      if (hasActivePaidSubscription) {
        navigate("/my-events");
      } else {
        navigate("/events");
      }
    } catch (error) {
      setErrorMessage("Failed to reset password. Please check your code & try again.");
    } finally {
      setLoading(false);
    }
  };

  // ----------------------------------------------------------------
  // RENDER
  // ----------------------------------------------------------------
  return (
    <div className="page-wrapper">
      <main className="main-wrapper">
        <section className="section_hero">
          <div className="padding-global padding-section-large">
            <div className="container-large">
              <div className="hero_card is-vertical" style={{ position: "relative" }}>
                <HelpFeedback />
                <h4 className="hero_heading-text text-align-center margin-bottom margin-small">
                  Log In
                </h4>
                <div className="custom-modal">
                  <div className="modal-content">
                    {/* ============================== */}
                    {/* Normal Login Form */}
                    {/* ============================== */}
                    {!showForgotForm && (
                      <form onSubmit={handleLogin}>
                        <label htmlFor="email">Email Address</label>
                        <input
                          className="hero_form-textfield w-input"
                          type="email"
                          id="email"
                          name="email"
                          required
                        />

                        <label htmlFor="password">Password</label>
                        <input
                          className="hero_form-textfield w-input"
                          type="password"
                          id="password"
                          name="password"
                          required
                        />

                        {errorMessage && (
                          <p className="error-message" style={{ color: "red" }}>
                            {errorMessage}
                          </p>
                        )}

                        <button
                          type="submit"
                          className="button-2 is-black w-button"
                          disabled={loading}
                          style={{ marginTop: "16px" }}
                        >
                          {loading ? "Logging In..." : "Log In"}
                        </button>

                        <p
                          onClick={handleShowForgot}
                          style={{
                            marginTop: "12px",
                            cursor: "pointer",
                            color: "blue",
                            textDecoration: "underline",
                          }}
                        >
                          Forgot your password?
                        </p>
                      </form>
                    )}

                    {/* ============================== */}
                    {/* Forgot Password -> Email Form */}
                    {/* ============================== */}
                    {showForgotForm && !showResetCodeForm && !showNewPasswordForm && (
                      <form onSubmit={handleForgotPassword}>
                        <h5>Reset Your Password</h5>
                        <label htmlFor="forgotEmail">Email Address</label>
                        <input
                          id="forgotEmail"
                          type="email"
                          className="hero_form-textfield w-input"
                          value={forgotEmail}
                          onChange={(e) => setForgotEmail(e.target.value)}
                          required
                        />
                        {errorMessage && (
                          <p className="error-message" style={{ color: "red" }}>
                            {errorMessage}
                          </p>
                        )}
                        {resetMessage && (
                          <p className="success-message" style={{ color: "green" }}>
                            {resetMessage}
                          </p>
                        )}
                        <button
                          type="submit"
                          className="button-2 is-black w-button"
                          disabled={loading}
                          style={{ marginTop: "16px" }}
                        >
                          {loading ? "Sending..." : "Send Reset Email"}
                        </button>
                        <button
                          type="button"
                          className="button-2 w-button"
                          onClick={handleShowForgot}
                          disabled={loading}
                          style={{ marginTop: "16px", marginLeft: "10px" }}
                        >
                          Back to Login
                        </button>
                      </form>
                    )}

                    {/* ============================== */}
                    {/* Enter Reset Code Form */}
                    {/* ============================== */}
                    {showForgotForm && showResetCodeForm && !showNewPasswordForm && (
                      <form onSubmit={handleResetCodeSubmit}>
                        <h5>Enter Reset Code</h5>
                        <label htmlFor="resetCode">Code (Check your email)</label>
                        <input
                          id="resetCode"
                          type="text"
                          className="hero_form-textfield w-input"
                          value={resetCode}
                          onChange={(e) => setResetCode(e.target.value)}
                          required
                        />
                        {errorMessage && (
                          <p className="error-message" style={{ color: "red" }}>
                            {errorMessage}
                          </p>
                        )}
                        {resetMessage && (
                          <p className="success-message" style={{ color: "green" }}>
                            {resetMessage}
                          </p>
                        )}
                        <button
                          type="submit"
                          className="button-2 is-black w-button"
                          disabled={loading}
                          style={{ marginTop: "16px" }}
                        >
                          Confirm Code
                        </button>
                      </form>
                    )}

                    {/* ============================== */}
                    {/* New Password Form */}
                    {/* ============================== */}
                    {showForgotForm && showResetCodeForm && showNewPasswordForm && (
                      <form onSubmit={handleNewPasswordSubmit}>
                        <h5>Set New Password</h5>
                        <label htmlFor="newPassword">New Password</label>
                        <input
                          id="newPassword"
                          type="password"
                          className="hero_form-textfield w-input"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          required
                        />

                        <label htmlFor="confirmNewPassword">Confirm New Password</label>
                        <input
                          id="confirmNewPassword"
                          type="password"
                          className="hero_form-textfield w-input"
                          value={confirmNewPassword}
                          onChange={(e) => setConfirmNewPassword(e.target.value)}
                          required
                        />

                        {errorMessage && (
                          <p className="error-message" style={{ color: "red" }}>
                            {errorMessage}
                          </p>
                        )}
                        {resetMessage && (
                          <p className="success-message" style={{ color: "green" }}>
                            {resetMessage}
                          </p>
                        )}

                        <button
                          type="submit"
                          className="button-2 is-black w-button"
                          disabled={loading}
                          style={{ marginTop: "16px" }}
                        >
                          {loading ? "Updating..." : "Submit New Password"}
                        </button>
                      </form>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default Login;
